/**
 * Permission vocabulary and role→permission map — phases.md §1.4.
 *
 * This is the canonical source. The `permissions` and `role_permissions` tables (Phase 1)
 * are a database mirror seeded from this file (Phase 2), so role→permission assignment is
 * a join at query time rather than a redeploy — but the vocabulary itself, and which roles
 * carry which permission, is code, not data. `requirePermission(...)` (Phase 3) reads the
 * database mirror; this file is what keeps that mirror honest.
 */
/** `entity:action`. Grouped by the entity they gate, per PRD §3/§24. */
export const PERMISSIONS = {
    // Articles — PRD §20/§21
    ARTICLE_CREATE: 'article:create',
    ARTICLE_EDIT_OWN: 'article:edit_own',
    ARTICLE_EDIT_ANY: 'article:edit_any',
    ARTICLE_DELETE: 'article:delete',
    ARTICLE_SUBMIT: 'article:submit',
    ARTICLE_REVIEW: 'article:review',
    ARTICLE_PUBLISH: 'article:publish',
    ARTICLE_SCHEDULE: 'article:schedule',
    ARTICLE_ARCHIVE: 'article:archive',
    // Taxonomy — PRD §8
    CATEGORY_MANAGE: 'category:manage',
    DISTRICT_MANAGE: 'district:manage',
    TAG_MANAGE: 'tag:manage',
    AUTHOR_MANAGE: 'author:manage',
    // Media — PRD §18
    MEDIA_UPLOAD: 'media:upload',
    MEDIA_MANAGE: 'media:manage',
    MEDIA_DELETE: 'media:delete',
    // Video — PRD §13
    VIDEO_MANAGE: 'video:manage',
    VIDEO_PUBLISH: 'video:publish',
    // Realtime — PRD §5/§12
    BREAKING_NEWS_MANAGE: 'breaking_news:manage',
    LIVE_EVENT_MANAGE: 'live_event:manage',
    // Curation — PRD §23
    HOMEPAGE_MANAGE: 'homepage:manage',
    CURATION_MANAGE: 'curation:manage',
    // Comments — PRD §35 (post-MVP, tables exist now)
    COMMENT_MODERATE: 'comment:moderate',
    // Advertising — PRD §17
    ADVERTISER_MANAGE: 'advertiser:manage',
    ADVERTISEMENT_MANAGE: 'advertisement:manage',
    // Newsletter — PRD §16
    NEWSLETTER_MANAGE: 'newsletter:manage',
    // Users, roles and sessions — PRD §24
    USER_MANAGE: 'user:manage',
    ROLE_MANAGE: 'role:manage',
    SESSION_MANAGE: 'session:manage',
    // Settings & SEO — PRD §19/§26
    SETTINGS_MANAGE: 'settings:manage',
    SEO_MANAGE: 'seo:manage',
    // Analytics & audit — PRD §25/§30
    ANALYTICS_VIEW: 'analytics:view',
    AUDIT_LOG_VIEW: 'audit_log:view',
};
export const ALL_PERMISSIONS = Object.values(PERMISSIONS);
/** PRD §24. `super_admin` and `admin` are near-identical in practice; the split exists so
 * exactly one account type can manage roles and sessions belonging to other admins. */
export const ROLES = {
    SUPER_ADMIN: 'super_admin',
    ADMIN: 'admin',
    EDITOR: 'editor',
    JOURNALIST: 'journalist',
    MODERATOR: 'moderator',
    AD_MANAGER: 'ad_manager',
};
const journalistPermissions = [
    PERMISSIONS.ARTICLE_CREATE,
    PERMISSIONS.ARTICLE_EDIT_OWN,
    PERMISSIONS.ARTICLE_SUBMIT,
    PERMISSIONS.MEDIA_UPLOAD,
    PERMISSIONS.MEDIA_MANAGE,
    PERMISSIONS.TAG_MANAGE,
    PERMISSIONS.VIDEO_MANAGE,
];
const editorPermissions = [
    ...journalistPermissions,
    PERMISSIONS.ARTICLE_EDIT_ANY,
    PERMISSIONS.ARTICLE_REVIEW,
    PERMISSIONS.ARTICLE_PUBLISH,
    PERMISSIONS.ARTICLE_SCHEDULE,
    PERMISSIONS.ARTICLE_ARCHIVE,
    PERMISSIONS.ARTICLE_DELETE,
    PERMISSIONS.CATEGORY_MANAGE,
    PERMISSIONS.DISTRICT_MANAGE,
    PERMISSIONS.AUTHOR_MANAGE,
    PERMISSIONS.MEDIA_DELETE,
    PERMISSIONS.VIDEO_PUBLISH,
    PERMISSIONS.BREAKING_NEWS_MANAGE,
    PERMISSIONS.LIVE_EVENT_MANAGE,
    PERMISSIONS.HOMEPAGE_MANAGE,
    PERMISSIONS.CURATION_MANAGE,
];
const moderatorPermissions = [PERMISSIONS.COMMENT_MODERATE];
const adManagerPermissions = [
    PERMISSIONS.ADVERTISER_MANAGE,
    PERMISSIONS.ADVERTISEMENT_MANAGE,
    PERMISSIONS.MEDIA_UPLOAD,
    PERMISSIONS.ANALYTICS_VIEW,
];
const adminPermissions = [
    ...editorPermissions,
    ...moderatorPermissions,
    ...adManagerPermissions,
    PERMISSIONS.NEWSLETTER_MANAGE,
    PERMISSIONS.USER_MANAGE,
    PERMISSIONS.SESSION_MANAGE,
    PERMISSIONS.SETTINGS_MANAGE,
    PERMISSIONS.SEO_MANAGE,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.AUDIT_LOG_VIEW,
];
/** Role → permission set. `super_admin` alone can manage roles and other admins' sessions. */
export const ROLE_PERMISSIONS = {
    [ROLES.SUPER_ADMIN]: ALL_PERMISSIONS,
    [ROLES.ADMIN]: dedupe(adminPermissions),
    [ROLES.EDITOR]: dedupe(editorPermissions),
    [ROLES.JOURNALIST]: dedupe(journalistPermissions),
    [ROLES.MODERATOR]: dedupe(moderatorPermissions),
    [ROLES.AD_MANAGER]: dedupe(adManagerPermissions),
};
function dedupe(values) {
    return Array.from(new Set(values));
}
export function roleHasPermission(role, permission) {
    return ROLE_PERMISSIONS[role].includes(permission);
}
//# sourceMappingURL=permissions.js.map